import { createLink, type LinkComponent } from "@tanstack/react-router";
import { buttonVariants } from "@ui/components/button";
import { cn } from "@ui/lib";
import type { VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

type AnchorButtonProps = React.ComponentProps<"a"> &
	VariantProps<typeof buttonVariants>;

const AnchorButton = forwardRef<HTMLAnchorElement, AnchorButtonProps>(
	({ className, variant, size, ...props }, ref) => (
		<a
			ref={ref}
			data-slot="button"
			className={cn(buttonVariants({ variant, size }), className)}
			{...props}
		/>
	),
);
AnchorButton.displayName = "AnchorButton";

const RouterLinkButton = createLink(AnchorButton);

/**
 * A router link that looks like a Button. Links keep link semantics (a Base UI
 * Button rendering an <a> becomes role="button"). Same variant and size props
 * as Button; everything else is a TanStack `Link`.
 *
 * `<LinkButton to="/app/$organizationSlug" params={…} variant="outline">Open</LinkButton>`
 */
export const LinkButton: LinkComponent<typeof AnchorButton> = (props) => (
	<RouterLinkButton preload="intent" {...props} />
);

/** An external or plain-href link styled as a Button. */
export function AnchorLinkButton(props: AnchorButtonProps) {
	return <AnchorButton {...props} />;
}
